import { createPortal } from "react-dom";
import { RotateCcw } from "lucide-react";
import { RadarFilterDrawer } from "@/components/radar/ui/RadarFilterDrawer";
import { radarI18nDe } from "@/components/radar/i18n";
import type { RadarBubble, RadarFilterState } from "@/services/api/teacher";

export function TalkRadarFilterSheet({
  open,
  onClose,
  filterState,
  setFilter,
  resetFilter,
  isLocked,
  role,
  incomingOff,
  bubbles,
  visibleCount,
  directorEnabled,
  minDays,
  maxDays,
  days,
  onDays,
}: {
  open: boolean;
  onClose: () => void;
  filterState: RadarFilterState;
  setFilter: (partial: Partial<RadarFilterState>) => void;
  resetFilter: () => void;
  isLocked: boolean;
  role: "admin" | "teacher";
  incomingOff: boolean;
  bubbles: RadarBubble[];
  visibleCount: number;
  directorEnabled: boolean;
  minDays: number;
  maxDays: number;
  days: number;
  onDays: (days: number) => void;
}) {
  if (!open) return null;
  const ft = radarI18nDe.filter;
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ft.title}
      className="talk-radar-sheet"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        top: "auto",
        zIndex: 60,
        maxHeight: "78vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        borderRadius: "22px 22px 0 0",
        background: "#081018",
        color: "#e2e8f0",
        padding: "12px 14px 18px",
      }}
    >
      <div
        className="flex items-center justify-between gap-2 shrink-0 pb-2"
        style={{ background: "#081018" }}
      >
        <h4 className="text-sm font-semibold shrink-0">{ft.title}</h4>
        <div className="flex items-center gap-1 flex-wrap justify-end">
          <button
            type="button"
            onClick={resetFilter}
            disabled={isLocked}
            className="flex items-center gap-1 text-[11px] px-1.5 py-1 disabled:opacity-40"
          >
            <RotateCcw className="w-3 h-3" /> {ft.reset}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] font-semibold px-1.5 py-1"
          >
            {ft.close}
          </button>
        </div>
      </div>
      <div style={{ overflowY: "auto", minHeight: 0, flex: 1 }}>
        <RadarFilterDrawer
          open
          onClose={onClose}
          filterState={filterState}
          setFilter={setFilter}
          resetFilter={resetFilter}
          isLocked={isLocked}
          radarType={role}
          showIncoming={role === "teacher" && !incomingOff}
          allBubbles={bubbles}
          totalCount={bubbles.length}
          visibleCount={visibleCount}
          t={radarI18nDe}
          directorEnabled={directorEnabled}
        />
        <label className="flex items-center gap-2 text-xs mt-3">
          {radarI18nDe.form.timeRange}
          <input
            type="range"
            min={minDays}
            max={maxDays}
            value={days}
            disabled={isLocked}
            aria-label={radarI18nDe.aria.timeRangeSlider}
            onChange={(event) => {
              if (isLocked) return;
              const next = Math.max(
                minDays,
                Math.min(maxDays, Number(event.target.value))
              );
              onDays(next);
            }}
          />
          <span>{days}d</span>
        </label>
      </div>
    </div>,
    document.body
  );
}
